import {
  ExtractionFieldType,
  ExtractionRequest,
  ExtractionType,
  ExtractorType,
  MatchLevel,
  PresetExtractor,
  RegexExtractor,
  TextExtractor,
} from 'dashboard/models/document';
import { RequiredExtraction } from 'dashboard/models/verification-check';
import {
  DocumentExtractionCheckConfig,
  isDocumentExtractionCheck,
} from 'dashboard/models/verification-check-config/document-extracted-properties-detection';
import { VerificationCheckConfig } from 'dashboard/models/verification-check-config/verification-check-config';
import { VerificationTemplateVersionDocument } from 'dashboard/models/verification-template-version';
import { FieldSchemaType } from 'lib/fields';
import { ApiModel } from 'lib/interfaces/api';
import { VerificationStatus } from 'lib/verification';
import { CheckName } from 'lib/verificationCheck';

export const DEFAULT_AI_MODEL = 'gpt-4o';

export const CONFIGURABLE_FIELD_SCHEMA_TYPES = [
  FieldSchemaType.Boolean,
  FieldSchemaType.Date,
  FieldSchemaType.Integer,
  FieldSchemaType.Number,
  FieldSchemaType.String,
];

export enum ExtractionCategory {
  Address = 'address',
  Name = 'name',
}

export enum MatchRequirement {
  Strict = 'strict',
  Loose = 'loose',
  LooseAi = 'loose_ai',
}

type ExtractionPanelFormBase = {
  fieldName: string;
  extractionType: ExtractionType;
  fieldType: ExtractionFieldType;
  extractorType: ExtractorType;

  isRequired: boolean;
};

type ExtractionPanelFormGuidedBase = ExtractionPanelFormBase & {
  // extraction category can be an inquiry field
  extractionCategory: ExtractionCategory | string;
  matchRequirement: MatchRequirement;
};

type ExtractionPanelFormGuidedAddress = ExtractionPanelFormGuidedBase & {
  addressStreet1Field: string;
  addressStreet2Field: string;
  addressCityField: string;
  addressSubdivisionField: string;
  addressPostalCodeField: string;
};

type ExtractionPanelFormGuidedName = ExtractionPanelFormGuidedBase & {
  nameFirstField: string;
  nameLastField: string;
};

type ExtractionPanelFormGuidedGA = ExtractionPanelFormGuidedAddress & ExtractionPanelFormGuidedName;

type ExtractionPanelFormGuided = ExtractionPanelFormBase & {
  matchLevelMinimum: MatchLevel;
};

type ExtractionPanelFormTransactionGuided = ExtractionPanelFormBase & {
  transactionTypeId: string;
  transactionFieldName: string;
  matchRequirement: MatchRequirement;
};

type ExtractionPanelFormUnguidedText = {
  textExtractors: Partial<TextExtractor>[];
};

type ExtractionPanelFormUnguidedRegex = {
  regexExtractors: Partial<RegexExtractor>[];
};

type ExtractionPanelFormUnguidedPreset = {
  presetExtractor: PresetExtractor;
};

type ExtractionPanelFormUnguidedKeyValuePair = {
  searchKeys: string[];
};

export enum UnguidedAiExtractionPreference {
  BestMatch = 'best_match',
  AllMatches = 'all_matches',
}

type ExtractionPanelFormUnguidedAi = {
  extractionPreference: UnguidedAiExtractionPreference;
  additionalInstructions: string;

  // TODO: Clean up `aiFieldType` and `extractionKeys` after Docs GA Product Readiness is fully launched
  aiFieldType: string;
  extractionKeys: string[];
};

type ExtractionPanelFormUnguided = ExtractionPanelFormBase &
  ExtractionPanelFormUnguidedText &
  ExtractionPanelFormUnguidedRegex &
  ExtractionPanelFormUnguidedPreset &
  ExtractionPanelFormUnguidedKeyValuePair &
  ExtractionPanelFormUnguidedAi;

export interface FieldWriteBackPanelForm {
  subfieldName?: string;
  isNewDocumentField: boolean;
  documentFieldName: string;
  documentFieldKey: string;
  documentFieldType: FieldSchemaType.String | FieldSchemaType.Array;
  // only for address and name extraction category
  displayName?: string;
}

export type ExtractionPanelForm = ExtractionPanelFormGuidedGA &
  ExtractionPanelFormGuided &
  ExtractionPanelFormTransactionGuided &
  ExtractionPanelFormUnguided & {
    version: '2.0' | '3.0';
    fieldName: string;
    extractionType: ExtractionType;
    fieldType: ExtractionFieldType;

    isRequired: boolean;

    saveToDocumentField: boolean;
    fieldWriteBackForms: FieldWriteBackPanelForm[];
  };

export const addExtractionToVerificationChecks = (
  vtmplVersion: VerificationTemplateVersionDocument,
  extractionRequest: ExtractionRequest,
): VerificationCheckConfig[] => {
  const verificationChecks = vtmplVersion?.attributes?.['verification-checks'] || [];
  if (vtmplVersion.type !== ApiModel.VerificationTemplateVersionDocument) {
    return verificationChecks;
  }
  const existingExtractedPropertiesDetection = verificationChecks.find(
    (v) => v.check === CheckName.DocumentExtractedPropertiesDetection,
  );
  let extractedPropertiesDetection: DocumentExtractionCheckConfig =
    existingExtractedPropertiesDetection &&
    isDocumentExtractionCheck(existingExtractedPropertiesDetection)
      ? existingExtractedPropertiesDetection
      : {
          check: CheckName.DocumentExtractedPropertiesDetection,
          'on-failed': VerificationStatus.Failed,
          'toggle-option': 'enabled',
          'required-extractions': [],
        };

  if (
    !extractedPropertiesDetection['required-extractions']?.some(
      (extraction) => extraction['field-name'] === extractionRequest['field-name'],
    )
  ) {
    const newRequiredExtraction: RequiredExtraction = {
      'field-name': extractionRequest['field-name'],
      'accept-condition': { '!==': [{ var: 'accepts' }, []] },
      name: extractionRequest['field-name'], // TODO: remove from form validation
    };
    extractedPropertiesDetection = {
      ...extractedPropertiesDetection,
      'toggle-option': 'enabled',
      'on-failed': VerificationStatus.Failed,
      'required-extractions': [
        ...(extractedPropertiesDetection?.['required-extractions'] || []),
        newRequiredExtraction,
      ],
    };

    return [
      ...verificationChecks.filter(
        (v) => v.check !== CheckName.DocumentExtractedPropertiesDetection,
      ),
      extractedPropertiesDetection,
    ];
  }

  return verificationChecks;
};

export const deleteExtractionFromVerificationChecks = (
  vtmplVersion: VerificationTemplateVersionDocument,
  extractionIndex: number,
): VerificationCheckConfig[] => {
  const verificationChecks = vtmplVersion?.attributes?.['verification-checks'] || [];
  if (vtmplVersion.type !== ApiModel.VerificationTemplateVersionDocument) {
    return verificationChecks;
  }
  const existingExtractedPropertiesDetection = verificationChecks.find(
    (v) => v.check === CheckName.DocumentExtractedPropertiesDetection,
  );
  let extractedPropertiesDetection =
    existingExtractedPropertiesDetection &&
    isDocumentExtractionCheck(existingExtractedPropertiesDetection)
      ? existingExtractedPropertiesDetection
      : undefined;
  if (!extractedPropertiesDetection) {
    return verificationChecks;
  }

  const fieldName = vtmplVersion?.attributes?.extractions?.[extractionIndex]?.['field-name'];
  const newRequiredExtractions = (
    extractedPropertiesDetection?.['required-extractions'] || []
  )?.filter((extraction) => extraction['field-name'] !== fieldName);
  const checkShouldBeRequired = newRequiredExtractions.length > 0;

  extractedPropertiesDetection = {
    ...extractedPropertiesDetection,
    'on-failed': checkShouldBeRequired ? VerificationStatus.Failed : VerificationStatus.Passed,
    'required-extractions': newRequiredExtractions,
  };

  return [
    ...verificationChecks.filter((v) => v.check !== CheckName.DocumentExtractedPropertiesDetection),
    extractedPropertiesDetection,
  ];
};

export const extractionUsesAi = (extraction: ExtractionRequest) => {
  if (extraction['extraction-type'] === ExtractionType.Unguided) {
    return extraction['extractor-type'] === ExtractorType.Ai;
  } else if (extraction['extraction-type'] === ExtractionType.Guided) {
    if (extraction.version === '2.0') {
      return !!extraction['ai-model'];
    } else if (extraction.version === '3.0') {
      return extraction.subfields.some((subfield) => subfield['ai-model']);
    }
  }
  return false;
};
